import {
  ListTemplatesRequest,
  GetTemplateRequest,
  CreateProjectRequest,
  GetProjectRequest,
  UpdateProjectPageRequest,
  ExportProjectRequest,
  ExportOptions,
  ListInstructionBackgroundsRequest,
  InstructionBackground,
  Template,
  GetTemplateResponse,
  Project,
  ProjectPageEdit,
} from "../generated/minibook/v1/minibook_pb";
import { MiniBookServiceClient } from "../generated/minibook/v1/MinibookServiceClientPb";
import { getGrpcEndpoint } from "./grpcEndpoint";

const GRPC_ENDPOINT = getGrpcEndpoint();

const client = new MiniBookServiceClient(GRPC_ENDPOINT);

export function getBaseUrl(): string {
  return GRPC_ENDPOINT;
}

/** Resolve image path from API (e.g. /static/uploads/...) to full URL using REACT_APP_API_URL */
export function resolveImageUrl(pathOrUrl: string): string {
  if (!pathOrUrl || typeof pathOrUrl !== "string") return pathOrUrl;
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) return pathOrUrl;
  const base = GRPC_ENDPOINT.replace(/\/$/, "");
  return pathOrUrl.startsWith("/") ? `${base}${pathOrUrl}` : `${base}/${pathOrUrl}`;
}

export interface TextBlock {
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  font: string;
  size: number;
  align: "left" | "center" | "right";
}

export interface EditJson {
  text_blocks?: TextBlock[];
  custom_image_url?: string;
  /** Clockwise image rotation in the page slot (0 / 90 / 180 / 270). */
  rotation_deg?: number;
}

export type PageRotationDeg = 0 | 90 | 180 | 270;

export function parseEditJson(raw: string | undefined | null): EditJson {
  if (!raw?.trim()) return { text_blocks: [] };
  try {
    const parsed = JSON.parse(raw) as EditJson;
    if (!parsed || typeof parsed !== "object") return { text_blocks: [] };
    return parsed;
  } catch {
    return { text_blocks: [] };
  }
}

export function normalizePageRotation(deg: number | undefined | null): PageRotationDeg {
  const d = ((deg ?? 0) % 360 + 360) % 360;
  if (d > 315 || d <= 45) return 0;
  if (d <= 135) return 90;
  if (d <= 225) return 180;
  return 270;
}

export function stepPageRotation(
  current: number | undefined | null,
  delta: 90 | -90,
): PageRotationDeg {
  return normalizePageRotation(normalizePageRotation(current) + delta);
}

export type PageLabelPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export type PaperSize = "LETTER" | "A4" | "A5" | "LEGAL";

export interface ExportOptionsInput {
  pageLabelPosition?: PageLabelPosition;
  showInstructions?: boolean;
  pageWidthInches?: number;
  pageHeightInches?: number;
  paperSize?: PaperSize;
  instructionBackgroundPath?: string;  // Path or URL to instruction background image
  includeDetailInstructions?: boolean;  // If true, append is_detail_instruction images as pages after page 1
  foldLineColorHex?: string;
  outlineLineColorHex?: string;
  cutLineColorHex?: string;
}

export type TemplateAsObject = Template.AsObject;

class MiniBookService {
  async listInstructionBackgrounds(
    activeOnly = true,
    isDetailInstruction?: boolean
  ): Promise<InstructionBackground.AsObject[]> {
    return new Promise((resolve, reject) => {
      const request = new ListInstructionBackgroundsRequest();
      request.setActiveOnly(activeOnly);
      if (isDetailInstruction !== undefined) request.setIsDetailInstruction(isDetailInstruction);
      client.listInstructionBackgrounds(request, null, (err, response) => {
        if (err) {
          console.error("Error listing instruction backgrounds:", err);
          reject(err);
          return;
        }
        resolve(response.getBackgroundsList().map((b) => b.toObject()));
      });
    });
  }

  async listTemplates(): Promise<Template.AsObject[]> {
    return new Promise((resolve, reject) => {
      const request = new ListTemplatesRequest();
      client.listTemplates(request, null, (err, response) => {
        if (err) {
          console.error("Error listing templates:", err);
          reject(err);
          return;
        }
        resolve(response.getTemplatesList().map((t) => t.toObject()));
      });
    });
  }

  async getTemplate(id: number): Promise<GetTemplateResponse.AsObject> {
    return new Promise((resolve, reject) => {
      const request = new GetTemplateRequest();
      request.setId(id);
      client.getTemplate(request, null, (err, response) => {
        if (err) {
          console.error("Error getting template:", err);
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  }

  async createProject(
    templateId: number,
    name: string,
    selectedPages: number[]
  ): Promise<number> {
    return new Promise((resolve, reject) => {
      const request = new CreateProjectRequest();
      request.setTemplateId(templateId);
      request.setName(name);
      selectedPages.forEach((p) => request.addSelectedPages(p));
      client.createProject(request, null, (err, response) => {
        if (err) {
          console.error("Error creating project:", err);
          reject(err);
          return;
        }
        resolve(response.getId());
      });
    });
  }

  async getProject(id: number): Promise<{
    project: Project.AsObject;
    pageEdits: ProjectPageEdit.AsObject[];
    pageLayout: { logicalPage: number; renderLabel: string }[];
  }> {
    return new Promise((resolve, reject) => {
      const request = new GetProjectRequest();
      request.setId(id);
      client.getProject(request, null, (err, response) => {
        if (err) {
          console.error("Error getting project:", err);
          reject(err);
          return;
        }
        const project = response.getProject();
        if (!project) {
          reject(new Error("Project not found"));
          return;
        }
        resolve({
          project: project.toObject(),
          pageEdits: response.getPageEditsList().map((e) => e.toObject()),
          pageLayout: response.getPageLayoutList().map((p) => ({ logicalPage: p.getLogicalPage(), renderLabel: p.getRenderLabel() })),
        });
      });
    });
  }

  async updateProjectPage(
    projectId: number,
    logicalPage: number,
    backgroundAssetId: number | undefined,
    editJson: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = new UpdateProjectPageRequest();
      request.setProjectId(projectId);
      request.setLogicalPage(logicalPage);
      request.setBackgroundAssetId(backgroundAssetId ?? 0);
      request.setEditJson(editJson);
      client.updateProjectPage(request, null, (err) => {
        if (err) {
          console.error("Error updating project page:", err);
          reject(err);
          return;
        }
        resolve();
      });
    });
  }

  async uploadPageImage(
    projectId: number,
    page: number,
    file: File
  ): Promise<{ url?: string; imageUrl?: string }> {
    const baseUrl = GRPC_ENDPOINT.replace(/\/$/, "");
    const url = `${baseUrl}/api/projects/${projectId}/pages/${page}/image`;
    const formData = new FormData();
    formData.append("image", file);
    const response = await fetch(url, {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Upload failed: ${response.status}`);
    }
    const text = await response.text();
    if (!text || !text.trim()) return {};
    try {
      const json = JSON.parse(text);
      return typeof json === "object" && json !== null ? json : {};
    } catch {
      return {};
    }
  }

  async exportProject(
    projectId: number,
    options?: ExportOptionsInput
  ): Promise<{
    pdfData: Uint8Array;
    filename: string;
    gridAdjusted: boolean;
    statusMessage: string;
  }> {
    return new Promise((resolve, reject) => {
      const request = new ExportProjectRequest();
      request.setProjectId(projectId);
      if (options) {
        const opts = new ExportOptions();
        if (options.pageLabelPosition) opts.setPageLabelPosition(options.pageLabelPosition);
        if (options.showInstructions !== undefined) opts.setShowInstructions(options.showInstructions);
        if (options.pageWidthInches !== undefined) opts.setPageWidthInches(options.pageWidthInches);
        if (options.pageHeightInches !== undefined) opts.setPageHeightInches(options.pageHeightInches);
        if (options.paperSize) opts.setPaperSize(options.paperSize);
        if (options.instructionBackgroundPath) opts.setInstructionBackgroundPath(options.instructionBackgroundPath);
        if (options.includeDetailInstructions !== undefined)
          opts.setIncludeDetailInstructions(options.includeDetailInstructions);
        if (options.foldLineColorHex) opts.setFoldLineColorHex(options.foldLineColorHex);
        if (options.outlineLineColorHex) opts.setOutlineLineColorHex(options.outlineLineColorHex);
        if (options.cutLineColorHex) opts.setCutLineColorHex(options.cutLineColorHex);
        request.setOptions(opts);
      }
      client.exportProject(request, null, (err, response) => {
        if (err) {
          console.error("Error exporting project:", err);
          reject(err);
          return;
        }
        const pdfData = response.getPdfData_asU8();
        const filename = response.getFilename() || "mini-book.pdf";
        const gridAdjusted = response.getGridAdjusted();
        const statusMessage = response.getStatusMessage() || "";
        resolve({ pdfData, filename, gridAdjusted, statusMessage });
      });
    });
  }
}

export const miniBookService = new MiniBookService();
